import { TrendingUp } from 'lucide-react';
import type React from 'react';
import { useMemo } from 'react';
import type { CustomerAgeingBandUI } from '@/types/ui/customer-detail';

interface AgeingBarChartProps {
  data: {
    bands: CustomerAgeingBandUI[];
    total: number;
    overdue: number;
  };
  currency?: string;
  className?: string;
  onSendReminders?: () => void;
  onEscalateToCollections?: () => void;
}

interface AgeingCategory {
  label: string;
  value: number;
  percentage: number;
  color: string;
  isOverdue: boolean;
  daysRange: string;
}

export const AgeingBarChart: React.FC<AgeingBarChartProps> = ({ data, currency = 'GBP', className = '' }) => {
  const { categories, total, overdueTotal, overduePercentage, currentPercentage, currentLabel } = useMemo(() => {
    const total = data.total;
    const overdueTotal = data.overdue;
    const overduePercentage = total > 0 ? (overdueTotal / total) * 100 : 0;
    const currentBand = data.bands.find((x) => x.startDays === 0);
    const currentPercentage = total > 0 && currentBand ? (currentBand.totalOutstanding / total) * 100 : 0;
    const currentLabel = currentBand?.label ?? 'Current';

    const categories: AgeingCategory[] = data.bands.map((x) => ({
      label: x.label,
      value: x.totalOutstanding,
      percentage: total > 0 ? (x.totalOutstanding / total) * 100 : 0,
      color: x.color,
      isOverdue: x.startDays > 0,
      daysRange: x.bandLabel,
    }));

    return { categories, total, overdueTotal, overduePercentage, currentPercentage, currentLabel };
  }, [data]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Header Summary - Compact */}
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-white rounded-lg shadow-sm">
            <TrendingUp className="h-4 w-4 text-slate-600" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Total Outstanding</div>
            <div className="text-xl font-bold text-foreground leading-tight">{formatCurrency(total)}</div>
          </div>
        </div>

        <div className="flex gap-5">
          <div className="text-right">
            <div className="text-xs text-muted-foreground">{currentLabel}</div>
            <div className="text-base font-semibold text-teal-600 leading-tight">
              {formatCurrency(total - overdueTotal)}
            </div>
            <div className="text-xs text-muted-foreground">{currentPercentage.toFixed(1)}%</div>
          </div>
          {overdueTotal > 0 && (
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Overdue</div>
              <div className="text-base font-semibold text-teal-600 leading-tight">{formatCurrency(overdueTotal)}</div>
              <div className="text-xs text-muted-foreground">{overduePercentage.toFixed(1)}%</div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Horizontal Bars - Compact */}
      <div className="space-y-1.5">
        {categories.map((category, index) => (
          <div key={index} className="group relative">
            {/* Row Container - Reduced padding */}
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-card hover:shadow-md transition-all duration-300 hover:border-slate-300">
              {/* Left: Label & Badge */}
              <div className="min-w-[170px] flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    {category.label}
                  </div>
                  <div className="text-xs text-muted-foreground">{category.daysRange}</div>
                </div>
              </div>

              {/* Center: Progress Bar - Reduced height */}
              <div className="flex-1 relative">
                <div className="h-7 bg-slate-100 rounded-md overflow-hidden border border-slate-200 shadow-inner">
                  {category.value > 0 && (
                    <div
                      className="h-full transition-all duration-700 ease-out flex items-center justify-between px-3 shadow-sm relative group-hover:shadow-md"
                      style={{
                        width: `${Math.max(category.percentage, 0)}%`,
                        minWidth: '50px',
                        backgroundColor: category.color,
                      }}
                    >
                      {/* Shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* Percentage inside bar */}
                      {category.percentage > 8 && (
                        <span className="text-xs font-bold text-white drop-shadow-md z-10 relative">
                          {category.percentage.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Amount & Percentage */}
              <div className="min-w-[130px] text-right">
                <div className="text-base font-bold text-foreground leading-tight">
                  {formatCurrency(category.value)}
                </div>
                {category.percentage <= 8 && category.value > 0 && (
                  <div className="text-xs font-medium text-muted-foreground">{category.percentage.toFixed(1)}%</div>
                )}
                {category.value === 0 && <div className="text-xs text-muted-foreground">0.0%</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
